import PropTypes from "prop-types";
import { useState, useEffect, useCallback } from "react";

import { isBrowser, createCookie, getCookie } from "@helpers/browser";
import { countdown } from "../hooks/useTimer";

import DefaultTimer from "./Timer/DefaultTimer";
import DateTimer from "./Timer/DateTimer";

function Timer({
  timerStartDate,
  timerEndDate,
  timerCountdown,
  timerCountdownInMinutes,
  cookieName = "timer",
}) {
  const TIMER_COOKIE = {
    expiration: 0,
    cookieName,
  };

  const [expiryTime, setExpiryTime] = useState(
    () =>
      getCookie(TIMER_COOKIE.cookieName) ??
      createCookie(TIMER_COOKIE.cookieName, TIMER_COOKIE.expiration, 3, true)
  );
  const showDateTimer =
    countdown(timerStartDate, timerEndDate) && window.exponea?.identified === "true";

  const onExpirationTime = useCallback(() => {
    if (!isBrowser) {
      return 0;
    }
    let timeExpiration;
    if (timerCountdownInMinutes) {
      timeExpiration = timerCountdownInMinutes;
    } else if (timerCountdown === "End of day") {
      const today = Date.now();
      const tomorrow = new Date().setHours(24, 0, 0, 0);
      timeExpiration = (tomorrow - today) / 1000 / 60;
    } else {
      timeExpiration = parseInt(timerCountdown, 10) * 60;
    }
    const cookie = createCookie(TIMER_COOKIE.cookieName, timeExpiration, 3, true);
    setExpiryTime(cookie);
    return cookie;
  }, [TIMER_COOKIE.cookieName, timerCountdown, timerCountdownInMinutes]);

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const cookieValue = getCookie(TIMER_COOKIE.cookieName);
    if (expiryTime !== cookieValue) {
      setExpiryTime(cookieValue);
    }

    if (expiryTime < new Date().getTime()) {
      onExpirationTime();
    }
  }, [expiryTime, onExpirationTime, cookieName, TIMER_COOKIE.cookieName]);

  // If customer is a first time visitor, we will display the default timer (1hr/6hr/end of day)
  return showDateTimer ? (
    <DateTimer
      onExpirationTime={onExpirationTime}
      timerStartDate={timerStartDate}
      timerEndDate={timerEndDate}
    />
  ) : (
    <DefaultTimer expiryTime={expiryTime} onExpirationTime={onExpirationTime} />
  );
}

export default Timer;

Timer.propTypes = {
  timerCountdown: PropTypes.string,
  timerStartDate: PropTypes.string,
  timerEndDate: PropTypes.string,
  cookieName: PropTypes.string.isRequired,
  timerCountdownInMinutes: PropTypes.number,
};
